import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { isPostHogInitialised } from './posthog';

const MAINTENANCE_MODE_FLAG = 'maintenance_mode';

const readFlag = (): boolean | undefined => {
  try {
    return posthog.isFeatureEnabled(MAINTENANCE_MODE_FLAG);
  } catch (error) {
    console.error('Failed to read the maintenance_mode feature flag', error);
    return undefined;
  }
};

/**
 * Reads the `maintenance_mode` PostHog feature flag (BIL-4826) without ever
 * blocking first paint. Returns `undefined` while the value is unknown —
 * including when PostHog was never initialised (no VITE_APP_POSTHOG_KEY:
 * local dev, CI) — so MaybeMaintenanceMode can fail open uniformly in both
 * cases (see its STOP condition: a kill switch that fails closed would
 * black out the product on a PostHog outage).
 *
 * PostHog's default `persistence` (localStorage+cookie, left untouched —
 * `advanced_disable_feature_flags` / `advanced_disable_decide` are never
 * set) caches the last-known flag value across page loads, so a returning
 * user gets that cached value at first paint and the fresh value once the
 * flags request resolves in the background — never a network round trip
 * gating render.
 */
export const useMaintenanceModeFlag = (): boolean | undefined => {
  const [flag, setFlag] = useState<boolean | undefined>(() =>
    isPostHogInitialised() ? readFlag() : undefined
  );

  useEffect(() => {
    if (!isPostHogInitialised()) return;

    try {
      // onFeatureFlags calls back immediately with the current (possibly
      // cached) flags in addition to future changes, and returns an
      // unsubscribe function — call it on unmount.
      const unsubscribe = posthog.onFeatureFlags(() => {
        setFlag(readFlag());
      });
      return () => {
        try {
          unsubscribe();
        } catch (error) {
          console.error(
            'Failed to unsubscribe from PostHog feature flag updates',
            error
          );
        }
      };
    } catch (error) {
      console.error(
        'Failed to subscribe to PostHog feature flag updates',
        error
      );
      return undefined;
    }
  }, []);

  return flag;
};
